import {
  Autocomplete,
  Badge,
  Box,
  Card,
  Divider,
  Grid,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import AngleRightIcon from "common/customIcons/angle_right";
import MDButton from "components/MDButton";
import colors from "assets/theme/base/colors";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Zoom from "react-medium-image-zoom";
import defaultProductImage from "../../../assets/images/placeholderproduct.svg";
import { capitalizeText } from "utilities/common";
import { isEmptyObject } from "utilities/common";
import { getData } from "utilities/common";
import { getPermissionsByMode } from "utilities/common";
import { hasSectionPermission } from "utilities/common";
import EditIcon from "common/customIcons/edit";
import dayjs from "dayjs";
import { useDispatch } from "react-redux";
import {
  closeCustomModalDialog,
  openCustomModalDialog,
  setModalLoader,
} from "../../../redux/slices/modalSlice";
import { openSnackbar } from "../../../redux/slices/common";
import DeleteIcon from "common/customIcons/delete";
import ActiveIcon from "common/customIcons/active";
import InActiveIcon from "common/customIcons/inactive";
import ProductTraits from "./productTraits";
import {
  deleteProductStrain,
  getAllHyperwolfStrains,
} from "../../../redux/slices/hyperwolf/strains";
import { addProductStrains, fetchSingleProduct } from "../../../redux/slices/hyperwolf/products";
import SeoContent from "./SeoContent";

export default function ProductDetailsPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { productId } = useParams();
  const { singleProduct = {}, loading } = useSelector((state) => state.hyperwolfProducts);
  const { productBatches = {} } = useSelector((state) => state.products);
  const [refreshLogs, setRefreshLogs] = useState(false);
  const { productDetails = {} } = singleProduct?.data || {};
  const [linkState, setLinkState] = useState("");

  let localUser = getData("login-user-info");
  if (localUser) {
    try {
      localUser = JSON.parse(localUser);
    } catch {
      localUser = null;
    }
  }
  const adminMode = localStorage.getItem("adminsMode")
    ? JSON.parse(localStorage.getItem("adminsMode"))
    : null;
  const permissionsForMode = getPermissionsByMode(localUser?.permissions, adminMode);
  const canEditProduct = hasSectionPermission(permissionsForMode, "Product", "Edit/Update");

  const {
    assets: detailsAssets,
    adminProductName: detailsProductName,
    websiteProductName,
    salePrice: detailsSalePrice,
    brandSlug: detailsBrandSlug,
    strainType: detailsStrainType,
    thcData,
    productDescription: detailsDescription,
    ingredientStatus,
    ingredients,
    instructionStatus,
    instructions,
    matchedStrains,
    productData = {},
    productSaleType: detailsProductSaleType,
  } = productDetails || {};

  const {
    unitPrice,
    thc,
    active,
    customWeight,
    weightPerUnit,
    customGramType,
    category,
    vendor,
    vendorName: dataVendorName,
    productSaleType: dataProductSaleType,
    description: dataDescription,
    name: dataName,
    flowerType,
    // cannabisType,
    tags: dataTags,
    salesPrice,
  } = productData || {};

  // Normalize images from productDetails.assets or productData.assets (objects with publicURL/origURL/largeURL or string URLs)
  const productImages = React.useMemo(() => {
    const list = detailsAssets ?? productData?.assets ?? [];
    return (Array.isArray(list) ? list : [])
      .map((a) => (typeof a === "string" ? a : a?.publicURL ?? a?.origURL ?? a?.largeURL ?? a?.mediumURL))
      .filter(Boolean);
  }, [detailsAssets, productData?.assets]);

  const adminProductName = detailsProductName ?? websiteProductName ?? dataName ?? "";
  const salePrice = detailsSalePrice ?? salesPrice;
  const brandSlug = detailsBrandSlug ?? productData?.brand?.name ?? "";
  const productDescription = detailsDescription ?? dataDescription ?? "";
  const productSaleType = detailsProductSaleType ?? dataProductSaleType ?? "";
  const vendorName = productData?.vendor?.name ?? dataVendorName ?? "";
  const productTags = dataTags ?? productDetails?.tags;
  const primaryStrainName = matchedStrains?.[0]?.strainName ?? flowerType ?? detailsStrainType ?? productDetails?.strainName ?? "";
  const productSku = productDetails?.sku ?? productData?.sku ?? "";
  const { grey } = colors;
  const formatValue = (value, emptyFallback = "-") => {
    if (value === null || value === undefined) return emptyFallback;
    if (typeof value === "string") {
      const normalized = value.trim().toLowerCase();
      if (!normalized || normalized === "-" || normalized === "--" || normalized === "null" || normalized === "undefined") {
        return emptyFallback;
      }
      return value;
    }
    if (typeof value === "number" || typeof value === "boolean") return String(value);
    if (Array.isArray(value)) {
      const items = value.map((v) => formatValue(v, "")).filter(Boolean);
      return items.length ? items.join(", ") : emptyFallback;
    }
    if (typeof value === "object") {
      const preferred =
        value?.name ||
        value?.title ||
        value?.label ||
        value?.slug ||
        value?.vendorName ||
        value?.brandName ||
        value?.strainName ||
        value?._id;
      if (preferred) return String(preferred);
      try {
        return JSON.stringify(value);
      } catch {
        return emptyFallback;
      }
    }
    return emptyFallback;
  };

  const safeHtml = (value) =>
    typeof value === "string" && value.trim() ? value : "<p>-</p>";
  const [isBatchAddOpen, setIsBatchAddOpen] = useState(false);
  const [editBatch, setEditBatch] = useState({});
  const [strainData, setStrainData] = useState([]);
  const [formData, setFormData] = useState({
    productId: "", // You might generate this automatically
    adminProductName: "",
    productImages: [],
    status: "inactive",
    isSalePrice: false,
    unitPrice: 0,
    salePrice: "",
    categoryName: "",
    brandSlug: "",
    // createdBy: user?.name || '',
    tags: [],
    strainSlug: "",
    strainType: "",
    infoEffects: [],
    categoryId: "",
    sku: "",
    brandDescription: "",
    productDescription: "",
    purchaseDate: undefined,
    uniqueNumber: "",
    expirationDate: null,
    trackingSystem: "",
    purchaseQuantity: "",
    currentQuantity: "",
    unitCost: "",
    batchStatus: "active",
    ingredientStatus: false,
    ingredients: "",
    instructionStatus: false,
    instructions: "",
    strainName: [], // Initialize as empty array
    subStrainSlug: [], // Initialize as empty array
    selectedStrains: [],
  });

  const showError = (message, errortype) => {
    if (errortype === "success") {
      dispatch(openSnackbar({ content: message, color: "success", icon: "check" }));
    }
    if (errortype === "error") {
      dispatch(openSnackbar({ content: message, color: "error", icon: "warning" }));
    }
  };

  const handleSave = () => {
    const strainArray = formData.strainName.map((strain, index) => ({
      strainName: strain,
      subStrainSlug: formData.subStrainSlug[index],
    }));

    dispatch(addProductStrains({ strainArray, productId }))
      .unwrap()
      .then((res) => {
        showError(res?.data?.message, "success");
        setFormData({
          ...formData,
          strainName: [], // Clear the strainName field after saving
          subStrainSlug: [], //
        });
        dispatch(fetchSingleProduct(productId)); // Refresh product data
      })
      .catch((err) => {
        showError(err?.message, "error");
      });
  };

  useEffect(() => {
    setTimeout(() => {
      setLinkState("");
    }, 1000);
  }, [linkState]);

  useEffect(() => {
    setRefreshLogs(true);
  }, [productBatches]);

  const queryParams = new URLSearchParams(location.search);
  const filter = queryParams.get("filter");
  useEffect(() => {
    if (filter === "activity_logs") {
      setIsBatchAddOpen(!isBatchAddOpen);
    }
  }, [filter]);

  useEffect(() => {
    if (!isEmptyObject(editBatch)) {
      const {
        purchaseDate,
        uniqueNumber,
        expirationDate,
        trackingSystem,
        purchaseQuantity,
        status,
        currentQuantity,
        unitCost,
      } = editBatch || {};
      setFormData({
        ...formData,
        ...{
          purchaseDate: dayjs(purchaseDate),
          uniqueNumber,
          expirationDate: expirationDate ? dayjs(expirationDate) : null,
          batchStatus: status,
          trackingSystem,
          purchaseQuantity,
          currentQuantity,
          unitCost,
        },
      });
    }
  }, [editBatch]);

  const handleAddNewStrain = () => {
    dispatch(
      openCustomModalDialog({
        modalData: { productId: productId, fetchProductStrainsData },
        modal: "ADD_NEW_STRAIN_MODAL",
      })
    );
  };

  const fetchProductStrainsData = () => {
    dispatch(fetchSingleProduct(productId)).unwrap();
  };

  useEffect(() => {
    const fetchStrains = async () => {
      try {
        const fetchStrainResponse = await dispatch(getAllHyperwolfStrains({ limit: 500 })).unwrap();
        const strainDataArray = fetchStrainResponse.strain;
        const filteredStrainData = strainDataArray.filter(
          (item) =>
            !matchedStrains.some((matchedItem) => matchedItem.strainName === item.strainName)
        );
        setStrainData(filteredStrainData);
      } catch (error) {}
    };
    fetchStrains();
  }, [formData.strainName, matchedStrains]);

  const deleteHandler = (deleteId) => {
    dispatch(setModalLoader(true));
    dispatch(deleteProductStrain({ productId, subStrainSlug: deleteId }))
      .unwrap()
      .then((res) => {
        showError(res?.data?.message, "success");
        dispatch(closeCustomModalDialog());
        dispatch(fetchSingleProduct(productId)).unwrap();
      })
      .catch((err) => {
        dispatch(setModalLoader(false));
        showError(err?.message, "error");
      });
  };

  return (
    <Box p={{ xs: 2, sm: 3, lg: 4 }} className="static-pages-container">
      <Stack direction="row" justifyContent="space-between" spacing={3} mb={3}>
        <Box display="flex" alignItems="center" color="text.main" lineHeight={1}>
          <IconButton
            onClick={() => {
              const q = location.state?.productsListQuery;
              if (q != null && String(q).trim() !== "") {
                navigate(`/hyperwolf/products?${q}`);
                return;
              }
              const s = location.state?.productsListSearch;
              if (s != null && String(s).trim() !== "") {
                navigate(
                  `/hyperwolf/products?search=${encodeURIComponent(String(s).trim())}`
                );
                return;
              }
              navigate("/hyperwolf/products");
            }}
          >
            <AngleRightIcon fontSize="14px" />
          </IconButton>
          <Typography variant="h6" fontWeight="semibold" ml={0.75}>
            Products
          </Typography>
        </Box>

      </Stack>
      {
        <>
          <Card sx={{ p: 3, mb: 4, borderRadius: 2.5, boxShadow: "none" }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={2}
              flexWrap="wrap"
              rowGap={1.5}
              mb={3}
            >
              <Typography variant="subtitle1" fontWeight="600">
                Product Information
              </Typography>
              <Box
                display="flex"
                alignItems="center"
                flexWrap="wrap"
                gap={1.5}
                justifyContent="flex-end"
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  gap={0.75}
                  lineHeight={1}
                  p={0.75}
                  borderRadius={2}
                >
                  {active && (
                    <Box color="#24CA49" display="flex" alignItems="center">
                      <ActiveIcon width="18px" height="18px" flex="none" />
                      <Typography
                        variant="caption"
                        fontWeight="regular"
                        ml={0.5}
                        textTransform="capitalize"
                      >
                        Active
                      </Typography>
                    </Box>
                  )}
                  {!active && (
                    <Box color="#FD4438" display="flex" alignItems="center">
                      <InActiveIcon width="18px" height="18px" flex="none" />
                      <Typography
                        variant="caption"
                        fontWeight="regular"
                        ml={0.5}
                        textTransform="capitalize"
                      >
                        Inactive
                      </Typography>
                    </Box>
                  )}
                </Stack>
                {canEditProduct && productId ? (
                  <MDButton
                    type="button"
                    variant="outlined"
                    color="primary"
                    size="small"
                    startIcon={<EditIcon width="15px" height="15px" />}
                    onClick={() =>
                      navigate(`/hyperwolf/products/edit/${productId}`, {
                        state: location.state,
                      })
                    }
                  >
                    Edit
                  </MDButton>
                ) : null}
              </Box>
            </Stack>

            {/* Product Images */}
            <Typography variant="body2" fontWeight="medium" color={grey[700]} sx={{ mb: 1.5 }}>
              Product Images
            </Typography>
            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mb: 4 }}>
              {productImages?.length > 0 ? (
                productImages?.map((productImageUrl) => (
                  <Zoom key={productImageUrl} classDialog="custom-zoom">
                    <img
                      style={{
                        borderRadius: "12px",
                        width: "120px",
                        height: "120px",
                        objectFit: "cover",
                      }}
                      src={productImageUrl}
                      alt="Product"
                    />
                  </Zoom>
                ))
              ) : (
                <img
                  style={{
                    borderRadius: "12px",
                    width: "120px",
                    height: "120px",
                    objectFit: "cover",
                  }}
                  src={defaultProductImage}
                  alt="No Image"
                />
              )}
            </Stack>

            {/* Product details – two-column label/value grid */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color={grey[600]} gutterBottom>
                  Product Name
                </Typography>
                <Box display="flex" alignItems="flex-start" gap={0.75}>
                  <Typography variant="body1" fontWeight="500">
                    {adminProductName ? capitalizeText(formatValue(adminProductName)) : "-"}
                  </Typography>
                  <svg
                    style={{ width: "18px", height: "18px", cursor: "pointer", flexShrink: 0 }}
                    xmlns="http://www.w3.org/2000/svg"
                    width="21"
                    height="21"
                    viewBox="0 0 21 21"
                    fill="none"
                    onClick={() => {
                      if (adminProductName) {
                        navigator.clipboard
                          .writeText(formatValue(adminProductName))
                          .then(() => setLinkState("Link copied!"))
                          .catch(() => {});
                      }
                    }}
                  >
                    <g clipPath="url(#clip0_14557_35992)">
                      <path
                        d="M8.61203 16.7513C6.65952 16.7513 5.07031 15.162 5.07031 13.2095V4.66797H3.61203C2.34784 4.66797 1.32031 5.69534 1.32031 6.95953V18.2095C1.32031 19.4737 2.34784 20.5013 3.61203 20.5013H14.0286C15.2928 20.5013 16.3203 19.4737 16.3203 18.2095V16.7513H8.61203Z"
                        fill="currentColor"
                      />
                      <path
                        d="M19.6536 2.79172C19.6536 1.52585 18.6277 0.5 17.362 0.5H8.61203C7.34616 0.5 6.32031 1.52585 6.32031 2.79172V13.2083C6.32031 14.4742 7.34616 15.5 8.61203 15.5H17.362C18.6277 15.5 19.6536 14.4742 19.6536 13.2083V2.79172Z"
                        fill="currentColor"
                      />
                    </g>
                    <defs>
                      <clipPath id="clip0_14557_35992">
                        <rect width="20" height="20" fill="currentColor" transform="translate(0.5 0.5)" />
                      </clipPath>
                    </defs>
                  </svg>
                  {linkState && <Typography variant="caption" color="text.secondary">{linkState}</Typography>}
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color={grey[600]} gutterBottom>Category</Typography>
                <Typography variant="body1" fontWeight="500">{formatValue(category?.name || category)}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color={grey[600]} gutterBottom>Brand</Typography>
                <Typography variant="body1" fontWeight="500">{formatValue(brandSlug)}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color={grey[600]} gutterBottom>Distributor</Typography>
                <Typography variant="body1" fontWeight="500">{formatValue(vendorName)}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color={grey[600]} gutterBottom>Weight Per Unit (WPU)</Typography>
                <Typography variant="body1" fontWeight="500">{formatValue(weightPerUnit)}</Typography>
              </Grid>
              {/* {customWeight != null && customWeight !== "" && (
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" color={grey[600]} gutterBottom>Custom Weight</Typography>
                  <Typography variant="body1" fontWeight="500">{customWeight}</Typography>
                </Grid>
              )}
              {customGramType ? (
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" color={grey[600]} gutterBottom>Custom Gram Type</Typography>
                  <Typography variant="body1" fontWeight="500">{customGramType}</Typography>
                </Grid>
              ) : null} */}
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color={grey[600]} gutterBottom>Retail Price</Typography>
                <Typography variant="body1" fontWeight="500">${unitPrice ?? 0}</Typography>
              </Grid>
              
                {/* <Typography variant="body2" color={grey[600]} gutterBottom>Sale Price</Typography> */}
               
              
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color={grey[600]} gutterBottom>Sell Type</Typography>
                <Typography variant="body1" fontWeight="500">{formatValue(productSaleType)}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color={grey[600]} gutterBottom>SKU</Typography>
                <Typography variant="body1" fontWeight="500">{formatValue(productSku)}</Typography>
              </Grid>
              {/* <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color={grey[600]} gutterBottom>Cannabis Type</Typography>
                <Typography variant="body1" fontWeight="500">{cannabisType || "-"}</Typography>
              </Grid> */}
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color={grey[600]} gutterBottom>Strain</Typography>
                <Typography variant="body1" fontWeight="500">{formatValue(primaryStrainName)}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color={grey[600]} gutterBottom>THC ({thcData?.includes("%") ? "%" : "mg"})</Typography>
                <Typography variant="body1" fontWeight="500">{formatValue(thc)}</Typography>
              </Grid>
              
            </Grid>

            <Grid item xs={12} sm={6} md={3} sx={{ mb: 3 }}>
                <Typography variant="body2" color={grey[600]} gutterBottom>Tags</Typography>
                <Typography variant="body1" fontWeight="500">
                  {Array.isArray(productTags) && productTags.length > 0
                    ? productTags.map((tag) => formatValue(tag, "")).filter(Boolean).join(", ")
                    : "-"}
                </Typography>
              </Grid>

            {/* Description */}
            <Typography variant="body2" fontWeight="medium" color={grey[600]} sx={{ mb: 1 }}>
              Description
            </Typography>
            <Box sx={{ pb: 2 }}>
              <Typography component="div" variant="body2" fontWeight="medium">
                {productDescription ? (
                  <div dangerouslySetInnerHTML={{ __html: safeHtml(productDescription) }} />
                ) : (
                  "-"
                )}
              </Typography>
            </Box>

            {ingredientStatus && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="body2" color={grey[600]} gutterBottom>Ingredients</Typography>
                <Box className="quill-text-editor-view" sx={{ p: 2, borderRadius: 1, bgcolor: grey[100] }}>
                  <Typography component="div" variant="body2" fontWeight="medium">
                    {ingredients ? <div dangerouslySetInnerHTML={{ __html: safeHtml(ingredients) }} /> : "-"}
                  </Typography>
                </Box>
              </Box>
            )}
            {instructionStatus && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="body2" color={grey[600]} gutterBottom>Instructions for use</Typography>
                <Box className="quill-text-editor-view" sx={{ p: 2, borderRadius: 1, bgcolor: grey[100] }}>
                  <Typography component="div" variant="body2" fontWeight="medium">
                    {instructions ? <div dangerouslySetInnerHTML={{ __html: safeHtml(instructions) }} /> : "-"}
                  </Typography>
                </Box>
              </Box>
            )}

            <Grid container spacing={2} sx={{ mt: 3 }}>
              <Grid item xs={12} className="mb-2">
                <Typography variant="body2" color={grey[600]}>
                  Genetics (Strains)
                </Typography>
              </Grid>
              <Grid
                item
                xs={12}
                className="bg-grey m-0 genetics-strains-sec"
              >
              <Grid container spacing={2} xs={12} md={12}>
                <Grid item={12}>
                  {matchedStrains?.map((strain, index) => (
                    <Badge key={index}>
                      <div className="badge-inner">
                        {strain.strainName}
                        <IconButton
                          onClick={() =>
                            dispatch(
                              openCustomModalDialog({
                                modalData: {
                                  deleteHandler,
                                  deleteId: strain.subStrainSlug,
                                  deleteType: "product strain",
                                },
                                modal: "DELETE_MODAL",
                              })
                            )
                          }
                        >
                          <DeleteIcon />
                        </IconButton>
                      </div>
                    </Badge>
                  ))}
                </Grid>
              </Grid>
              <Grid container xs={12} md={12} className="mt-3 align-center select-multi-strain">
                <Grid item xs={12}>
                  <div className="select-strain-sec">
                    <Autocomplete
                      multiple
                      id="strain-autocomplete"
                      options={[{ strainName: "+ Add new strain" }, ...(strainData || [])]}
                      getOptionLabel={(option) => option.strainName}
                      filterSelectedOptions
                      onChange={(event, selectedOptions) => {
                        const hasAddNew = selectedOptions.some(
                          (opt) => opt.strainName === "+ Add new strain"
                        );
                        if (hasAddNew) {
                          handleAddNewStrain();
                          // Remove "Add new strain" from selected options to prevent it from being displayed
                          const filteredOptions = selectedOptions.filter(
                            (opt) => opt.strainName !== "+ Add new strain"
                          );
                          setFormData((prev) => ({
                            ...prev,
                            strainName: filteredOptions.map((opt) => opt.strainName),
                            subStrainSlug: filteredOptions.map((opt) => opt.subStrainSlug),
                          }));
                        } else {
                          setFormData((prev) => ({
                            ...prev,
                            strainName: selectedOptions.map((opt) => opt.strainName),
                            subStrainSlug: selectedOptions.map((opt) => opt.subStrainSlug),
                          }));
                        }
                      }}
                      value={formData.strainName.map(
                        (name) =>
                          strainData.find((strain) => strain.strainName === name) || {
                            strainName: name,
                          }
                      )}
                      renderInput={(params) => <TextField {...params} label="Select Strain" />}
                      renderOption={(props, option) => (
                        <li
                          {...props}
                          style={{
                            color: option.strainName === "+ Add new strain" ? "#0163FF" : "inherit", // Apply blue color to "Add new strain"
                          }}
                        >
                          {option.strainName}
                        </li>
                      )}
                    />
                  </div>
                </Grid>

                {/* Edit Icon Button */}
                {formData.strainName.length > 0 && (
                  <MDButton
                    onClick={handleSave}
                    size="small"
                    className="mr-3 primay-btn"
                    color="primary"
                    loading={loading}
                    disabled={loading}
                  >
                    Save
                  </MDButton>
                )}
              </Grid>
            </Grid>
            </Grid>

            
          </Card>
        </>
      }

<ProductTraits />
      {/* meta properties card */}
      <SeoContent productDetails={productDetails} />
     
    </Box>
  );
}
